import {Fragment} from "react";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {InfoTooltip} from "./InfoTooltip";
import {TooltipSummary} from "./TooltipSummary";

type ParameterDescription = {summary: string; states?: {term: string; description: string}[]};

const PARAMETER_DESCRIPTIONS: Record<string, ParameterDescription> = {
  credit_reporting_state: {
    summary: "Where the account sits in the credit reporting cycle.",
    states: [
      {
        term: "Before",
        description:
          "Partner reports to bureaus and reporting is scheduled to start within the next 31 days (no statement created yet).",
      },
      {
        term: "After",
        description: "Currently being reported - a statement exists and the account hasn't been deleted.",
      },
      {
        term: "Eligible",
        description:
          "Partner reports and a start date is set, but the account is neither in the before-window nor already reporting.",
      },
      {
        term: "Not reporting",
        description: "Partner doesn't report to bureaus, or no reporting start date is set.",
      },
    ],
  },
  pre_legal: {
    summary:
      "True when the account is in pre-legal collections (legal-collection status = PRE_LEGAL) - the early stage before formal legal action.",
  },
  in_validation_period: {
    summary: "Based on when the account's validation notice was sent (validated timestamp).",
    states: [
      {term: "True", description: "The account was validated more than 45 days ago."},
      {term: "False", description: "The account was validated within the last 45 days, or was never validated."},
    ],
  },
  has_installment_plans: {
    summary: "Whether the plan offers shown to the consumer include any installment plan, not just one-time payment.",
    states: [
      {
        term: "True",
        description: "At least one monthly, weekly, bi-weekly or 1st & 15th plan is offered.",
      },
      {
        term: "False",
        description: "Only one-time (or hardship) offers, or plan offers haven't been calculated for the account yet.",
      },
    ],
  },
  in_multi_validation: {
    summary: "Whether the account is part of today's multi-account validation send.",
    states: [
      {term: "True", description: "A multi-account validation communication was queued for the account today."},
      {term: "False", description: "No multi-account validation communication was queued for the account today."},
    ],
  },
  is_pre_charge_off: {
    summary: "Whether the account's partner places accounts before charge-off.",
    states: [
      {term: "True", description: "The account's partner has pre-charge-off enabled."},
      {term: "False", description: "The account's partner places charged-off accounts."},
    ],
  },
  days_past_due: {
    summary: "Days since the account first became delinquent. Tracked only for pre-charge-off accounts.",
  },
  batch_cycle: {
    summary: "The placement cycle the account belongs to, read from its batch name.",
    states: [
      {term: "Primary", description: 'First placement wave (batch name contains "_PRI").'},
      {term: "Secondary", description: 'Second placement wave (batch name contains "_SEC").'},
      {term: "Tertiary", description: 'Third placement wave (batch name contains "_TER").'},
      {term: "Unknown", description: "Batch name doesn't match any known wave pattern."},
    ],
  },
  one_time_discount: {
    summary:
      "The highest discount (0–1) among the one-time payment offers shown to the consumer, based on the payment_plan_offers table.",
  },
  longest_plan_discount: {
    summary:
      "The discount (0–1) on the longest monthly plan offered to the consumer, based on the payment_plan_offers table.",
  },
  max_plan_length_sif: {
    summary:
      "The longest monthly Settle in Full plan (in months) offered to the consumer, based on the payment_plan_offers table.",
  },
  max_plan_length_pif: {
    summary:
      "The longest monthly Pay in Full plan (in months) offered to the consumer, based on the payment_plan_offers table.",
  },
  current_creditor: {
    summary: "The account's current creditor name. Matching ignores case and surrounding whitespace.",
  },
};

const StatePill = ({term}: {term: string}) => (
  <Box
    sx={{
      justifySelf: "start",
      color: Colors.blue._800,
      backgroundColor: Colors.blue._100,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "6px",
      px: 0.75,
      py: 0.25,
      whiteSpace: "nowrap",
    }}
  >
    {term}
  </Box>
);

const ParameterContent = ({description}: {description: ParameterDescription}) => (
  <Box sx={{py: 0.25}}>
    <TooltipSummary>{description.summary}</TooltipSummary>
    {description.states && (
      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          columnGap: 1,
          rowGap: 0.75,
          alignItems: "start",
        }}
      >
        {description.states.map((s) => (
          <Fragment key={s.term}>
            <StatePill term={s.term} />
            <Text size={12} sx={{color: Colors.grey._600, lineHeight: 1.45}}>
              {s.description}
            </Text>
          </Fragment>
        ))}
      </Box>
    )}
  </Box>
);

export const ParameterInfoTooltip = ({parameter}: {parameter: string}) => {
  const description = PARAMETER_DESCRIPTIONS[parameter];
  if (!description) return null;
  return <InfoTooltip content={<ParameterContent description={description} />} placement="right" />;
};
